import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import { I18nextProvider, useTranslation } from 'react-i18next';

import { LangCodeEnum } from 'shared/enums/LangCodeEnum';
import { getLanguageDirection } from 'shared/utils/getLanguageDirection';
import { getLanguageFromLocale } from 'shared/utils/getLanguageFromLocale';
import { getInitialLanguage } from 'translations/getInitialLanguage';

const LanguageContext = createContext<{
  language: LangCodeEnum;
  changeLanguage: (language: LangCodeEnum) => Promise<void>;
  setChangeLanguageCallback: (
    changeLanguageCallback: (locale: LangCodeEnum) => Promise<void>
  ) => void;
}>({
  language: LangCodeEnum.ENGLISH_BRITISH,
  changeLanguage: () => Promise.resolve(),
  setChangeLanguageCallback: () => {},
});

export const useLanguage = () => {
  return useContext(LanguageContext);
};

type LanguageProviderProps = {
  children: React.ReactNode;
};

export const LanguageProvider: React.VFC<LanguageProviderProps> = ({ children }) => {
  const { i18n } = useTranslation();
  const initialLanguage = getInitialLanguage();
  const [language, setLanguage] = useState(initialLanguage);
  document.documentElement.dir = getLanguageDirection(language);
  document.documentElement.lang = getLanguageFromLocale(language);

  type ChangeLanguageCallbackRefType = (locale: LangCodeEnum) => Promise<void>;
  const changeLanguageCallbackRef = useRef<ChangeLanguageCallbackRefType>(() => Promise.resolve());

  const setChangeLanguageCallback = (changeLanguageCallback: ChangeLanguageCallbackRefType) => {
    changeLanguageCallbackRef.current = changeLanguageCallback;
  };

  const changeLanguage = useCallback(
    async (language: LangCodeEnum) => {
      await i18n.changeLanguage(language);
      await changeLanguageCallbackRef.current(language);
      document.documentElement.dir = getLanguageDirection(language);
      document.documentElement.lang = getLanguageFromLocale(language);
      setLanguage(language);
    },
    [i18n]
  );

  return (
    <I18nextProvider i18n={i18n}>
      <LanguageContext.Provider value={{ language, changeLanguage, setChangeLanguageCallback }}>
        {children}
      </LanguageContext.Provider>
    </I18nextProvider>
  );
};
